import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import KPICard from "@/components/KPICard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/format";
import {
    DollarSign, Users, Truck, AlertTriangle, TrendingUp, Route as RouteIcon,
    Package, Clock,
} from "lucide-react";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell,
} from "recharts";
import { Link } from "react-router-dom";

export default function Dashboard() {
    const [kpis, setKpis] = useState(null);
    const [recent, setRecent] = useState({ invoices: [], rolloff: [], customers: [], comms: [] });

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/kpis"),
            api.get("/invoices?status=overdue"),
            api.get("/rolloff?status=scheduled"),
            api.get("/customers"),
            api.get("/communications"),
        ]).then(([k, inv, ro, cu, co]) => {
            setKpis(k.data);
            setRecent({
                invoices: inv.data.slice(0, 5),
                rolloff: ro.data.slice(0, 5),
                customers: cu.data.slice(0, 5),
                comms: co.data.slice(0, 6),
            });
        }).catch(console.error);
    }, []);

    const chartColors = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
                         "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                title="Operations Today"
                subtitle="The state of every truck, route, customer and invoice — in one view."
                actions={
                    <>
                        <Link to="/routing"><Button variant="outline" size="sm" data-testid="header-routing-btn">View routes</Button></Link>
                        <Link to="/billing"><Button size="sm" data-testid="header-billing-btn">Open billing</Button></Link>
                    </>
                }
            />

            <div className="px-8 py-6 space-y-6">
                {/* KPIs */}
                <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-kpis">
                    {kpis ? (
                        <>
                            <KPICard testid="kpi-revenue-mtd" label="Revenue MTD"
                                     value={fmtMoney(kpis.revenue_mtd)} icon={DollarSign}
                                     delta={`${fmtMoney(kpis.revenue_today)} today`} deltaTone="success" />
                            <KPICard testid="kpi-outstanding" label="Outstanding A/R"
                                     value={fmtMoney(kpis.outstanding)} icon={AlertTriangle}
                                     delta="Across open + overdue" deltaTone="muted" />
                            <KPICard testid="kpi-active-customers" label="Active customers"
                                     value={kpis.active_customers} icon={Users}
                                     delta={`+${kpis.new_customers_7d} this week`} deltaTone="success" />
                            <KPICard testid="kpi-fleet" label="Fleet status"
                                     value={`${kpis.trucks_active} / ${kpis.trucks_active + kpis.trucks_maintenance}`} icon={Truck}
                                     delta={`${kpis.trucks_maintenance} in maintenance`}
                                     deltaTone={kpis.trucks_maintenance > 0 ? "destructive" : "muted"} />
                            <KPICard testid="kpi-today-routes" label="Today's routes"
                                     value={kpis.todays_routes} icon={RouteIcon} delta="Scheduled" />
                            <KPICard testid="kpi-rolloff-today" label="Roll-off jobs today"
                                     value={kpis.rolloff_today} icon={Package} delta="Deliveries + swaps" />
                            <KPICard testid="kpi-on-time" label="On-time rate" value="98.4%" icon={Clock}
                                     delta="Last 7 days" deltaTone="success" />
                            <KPICard testid="kpi-growth" label="Growth (30d)" value="+8.2%" icon={TrendingUp}
                                     delta="vs prior month" deltaTone="success" />
                        </>
                    ) : (
                        Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="border border-border rounded-md p-5 surface">
                                <Skeleton className="h-3 w-20 mb-4" />
                                <Skeleton className="h-9 w-32" />
                                <Skeleton className="h-3 w-24 mt-3" />
                            </div>
                        ))
                    )}
                </section>

                {/* Charts */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-2" data-testid="revenue-trend-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="overline mb-1">Revenue trend</div>
                                    <h3 className="font-heading text-lg font-semibold">Last 30 days</h3>
                                </div>
                                <div className="text-right">
                                    <div className="font-heading text-2xl font-bold tabular">{fmtMoney(kpis?.revenue_mtd || 0)}</div>
                                    <div className="overline mt-1">Month to date</div>
                                </div>
                            </div>
                            <div style={{ width: "100%", height: 220, minHeight: 220 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={kpis?.revenue_trend || []}>
                                        <defs>
                                            <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} interval={6} />
                                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                                        <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="revenue-by-type-card">
                        <CardContent className="p-6">
                            <div className="overline mb-1">Revenue mix</div>
                            <h3 className="font-heading text-lg font-semibold mb-6">By service type</h3>
                            <div style={{ width: "100%", height: 220, minHeight: 220 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={kpis?.revenue_by_type || []} layout="vertical" margin={{ left: 70 }}>
                                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                                        <YAxis type="category" dataKey="type" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                                        <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                                            {(kpis?.revenue_by_type || []).map((_, i) => (
                                                <Cell key={i} fill={chartColors[i % chartColors.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* Activity lists */}
                <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card data-testid="overdue-invoices-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="overline mb-1">Overdue</div>
                                    <h3 className="font-heading text-lg font-semibold">Outstanding invoices</h3>
                                </div>
                                <Link to="/billing" className="text-xs font-medium text-primary hover:underline">View all →</Link>
                            </div>
                            <div className="divide-y divide-border">
                                {recent.invoices.length === 0 && <div className="text-sm text-muted-foreground py-4">All clear — nothing overdue.</div>}
                                {recent.invoices.map((inv) => (
                                    <div key={inv.id} className="py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{inv.customer_name}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{inv.invoice_number} · due {fmtDate(inv.due_date)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-semibold tabular">{fmtMoney(inv.total)}</div>
                                            <StatusPill status={inv.status} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="rolloff-pending-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="overline mb-1">Scheduled</div>
                                    <h3 className="font-heading text-lg font-semibold">Roll-off jobs upcoming</h3>
                                </div>
                                <Link to="/rolloff" className="text-xs font-medium text-primary hover:underline">View board →</Link>
                            </div>
                            <div className="divide-y divide-border">
                                {recent.rolloff.length === 0 && <div className="text-sm text-muted-foreground py-4">No jobs scheduled.</div>}
                                {recent.rolloff.map((j) => (
                                    <div key={j.id} className="py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{j.customer_name}</div>
                                            <div className="text-xs text-muted-foreground">{j.job_type.replace(/_/g, " ")} · {j.container_size}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs font-mono">{fmtDate(j.scheduled_date)}</div>
                                            <StatusPill status={j.status} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card data-testid="recent-customers-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="overline mb-1">Just added</div>
                                    <h3 className="font-heading text-lg font-semibold">Recent customers</h3>
                                </div>
                                <Link to="/customers" className="text-xs font-medium text-primary hover:underline">View CRM →</Link>
                            </div>
                            <div className="divide-y divide-border">
                                {recent.customers.map((c) => (
                                    <div key={c.id} className="py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{c.name}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{c.account_number} · {c.type}</div>
                                        </div>
                                        <div className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="activity-feed-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="overline mb-1">Live feed</div>
                                    <h3 className="font-heading text-lg font-semibold">Activity</h3>
                                </div>
                                <Link to="/communications" className="text-xs font-medium text-primary hover:underline">Full log →</Link>
                            </div>
                            <div className="space-y-3">
                                {recent.comms.map((c) => (
                                    <div key={c.id} className="flex items-start gap-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm">{c.subject || c.body.slice(0, 60)}</div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                {c.channel.toUpperCase()} · {fmtDateTime(c.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </section>
            </div>
        </div>
    );
}
